import type { TypedDocumentNode } from '@apollo/client';
import { gql, type QueryHookOptions, useQuery } from '@apollo/client';
import type { Query, QueryAssetsConnectionArgs } from '@/types/GraphqlSchema';

const ASSETS_LIST_FIELDS = `
  id
  legacyId
  displayName
  country {
    id
    displayName
    isoCode
  }
  reportUpdatedDate
  mAndAOpportunity
  formattedNpv
  formattedRemainingReserves
`;

const ASSETS_LIST_QUERY: TypedDocumentNode<AssetsConnectionQueryData, QueryAssetsConnectionArgs> =
  gql`
  query assetsListConnection(
    $assetLegacyIds: [ID!]
    $assetIsoCodes: [String!]
    $country: [String!]
    $company: [String!]
    $basin: [String!]
    $developmentStatus: [ID!]
    $developmentType: [ID!]
    $shoreStatus: [String!]
    $operator: Boolean
    $opportunities: Boolean
    $geologyEpochIds: [ID!]
    $hydrocarbonType: [String!]
    $includeHistoricalParticipants: Boolean
    $nonOperator: Boolean
    $companiesByAndOperator: Boolean
    $oilType: [String!]
    $npvMin: Float
    $npvMax: Float
    $reservesMin: Float
    $reservesMax: Float
    $productionMin: Float
    $productionMax: Float
    $depthMin: Float
    $depthMax: Float
    $isWalkthrough: Boolean
    $sortType: String!
    $sortAscending: Boolean!
    $first: Int
    $after: String
  ) {
    assetsConnection(
      assetLegacyIds: $assetLegacyIds
      assetIsoCodes: $assetIsoCodes
      country: $country
      company: $company
      basin: $basin
      developmentStatus: $developmentStatus
      developmentType: $developmentType
      shoreStatus: $shoreStatus
      operator: $operator
      opportunities: $opportunities
      geologyEpochIds: $geologyEpochIds
      hydrocarbonType: $hydrocarbonType
      includeHistoricalParticipants: $includeHistoricalParticipants
      nonOperator: $nonOperator
      companiesByAndOperator: $companiesByAndOperator
      oilType: $oilType
      npvMin: $npvMin
      npvMax: $npvMax
      reservesMin: $reservesMin
      reservesMax: $reservesMax
      productionMin: $productionMin
      productionMax: $productionMax
      depthMin: $depthMin
      depthMax: $depthMax
      isWalkthrough: $isWalkthrough
      sortType: $sortType
      sortAscending: $sortAscending
      first: $first
      after: $after
    ) {
      totalCount
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          ${ASSETS_LIST_FIELDS}
        }
      }
    }
  }
`;

type AssetsConnectionQueryData = Pick<Query, 'assetsConnection'>;

export const useAssets = (
  variables: QueryAssetsConnectionArgs,
  options: QueryHookOptions<AssetsConnectionQueryData, QueryAssetsConnectionArgs> = {},
) => {
  const queryOptions: QueryHookOptions<AssetsConnectionQueryData, QueryAssetsConnectionArgs> = {
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
    ...options,
    variables,
  };

  return useQuery(ASSETS_LIST_QUERY, queryOptions);
};
